// Attribution utilities for tracking how users arrive at forms.
// Server-side: schema + cleanUrl for the API route.
// Client-side: localStorage capture + retrieval (see captureAttribution / getAttribution).

import { z } from 'zod'
import { createLogger } from '@/lib/logger'

const logger = createLogger('attribution')
const FIRST_TOUCH_KEY = 'eff_first_touch'
const LAST_TOUCH_KEY = 'eff_last_touch'

export const TRACKING_PARAMS = new Set([
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
  'gclid',
  'fbclid',
  'msclkid',
  'ttclid',
  'twclid',
  'ref',
  'ref_src',
])

export const attributionSchema = z.object({
  landingUrlClean: z.string(),
  referrer: z.string().nullable(),
  referral: z.string().nullable().optional().default(null),
  utmSource: z.string().nullable(),
  utmMedium: z.string().nullable(),
  utmCampaign: z.string().nullable(),
  utmTerm: z.string().nullable(),
  utmContent: z.string().nullable(),
  capturedAt: z.string(),
})

export type AttributionPayload = z.infer<typeof attributionSchema>

export function cleanUrl(url: URL): string {
  const cleaned = new URL(url.origin + url.pathname)

  url.searchParams.forEach((value, key) => {
    if (!TRACKING_PARAMS.has(key)) {
      cleaned.searchParams.set(key, value)
    }
  })

  return cleaned.toString()
}

function buildAttributionPayload(): AttributionPayload | null {
  const url = new URL(window.location.href)
  const origin = url.origin

  const referral = url.searchParams.get('ref') || null

  let referrer: string | null = null
  if (document.referrer) {
    try {
      const refererUrl = new URL(document.referrer)
      if (refererUrl.origin !== origin) {
        referrer = document.referrer
      }
    } catch {
      referrer = document.referrer
    }
  }

  const payload: AttributionPayload = {
    landingUrlClean: cleanUrl(url),
    referrer,
    referral,
    utmSource: url.searchParams.get('utm_source'),
    utmMedium: url.searchParams.get('utm_medium'),
    utmCampaign: url.searchParams.get('utm_campaign'),
    utmTerm: url.searchParams.get('utm_term'),
    utmContent: url.searchParams.get('utm_content'),
    capturedAt: new Date().toISOString(),
  }

  const hasData = Boolean(
    payload.utmSource ||
      payload.utmMedium ||
      payload.utmCampaign ||
      payload.utmTerm ||
      payload.utmContent ||
      payload.referrer ||
      payload.referral,
  )

  return hasData ? payload : null
}

// Call once on app mount (e.g., in root layout client component).
// Sets first-touch if not already stored, always updates last-touch.
export function captureAttribution(): void {
  if (typeof window === 'undefined') return

  try {
    const payload = buildAttributionPayload()
    if (!payload) return

    const serialized = JSON.stringify(payload)

    if (!localStorage.getItem(FIRST_TOUCH_KEY)) {
      localStorage.setItem(FIRST_TOUCH_KEY, serialized)
    }

    localStorage.setItem(LAST_TOUCH_KEY, serialized)
  } catch {
    // localStorage unavailable (private browsing, storage full, etc.)
  }
}

// Read first-touch attribution for form submissions.
export function getAttribution(): AttributionPayload | null {
  if (typeof window === 'undefined') return null

  try {
    const raw = localStorage.getItem(FIRST_TOUCH_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw)
    const result = attributionSchema.safeParse(parsed)
    if (!result.success) {
      logger.error('Failed to parse stored attribution data:', result.error.flatten())
      return null
    }
    return result.data
  } catch (error) {
    logger.error('Failed to read attribution from localStorage:', error)
    return null
  }
}
